import { create } from 'zustand';
import type { CharacterPayload } from '@client/components';

interface StoreState {
  max_alternatives: number;
  tokenTotal: number;
  permanentTokens: number;
  temporaryTokens: number;
  error: string | null;
  gallery: string[];
  creatorNotes: string;
}

interface CharacterActions {
  setCharacter: (
    data:
      | Partial<CharacterPayload & StoreState>
      | ((prev: CharacterPayload & StoreState) => CharacterPayload & StoreState)
  ) => void;
  setGender: (gender: string) => void;
  setTokenTotal: (tokenTotal: number) => void;
  setPermanentTokens: (tokens: number) => void;
  setTemporaryTokens: (tokens: number) => void;
  setError: (error: string | null) => void;
  setFirstMessageAlternatives: (alternatives: string[]) => void;
  setMaxAlternatives: (max: number) => void;
  loadDraft: (draft: CharacterPayload) => void;
  addGalleryImages: (urls: string[]) => void;
  setCreatorNotes: (notes: string) => void;
}

export const useCharacterStore = create<
  CharacterPayload & StoreState & CharacterActions
>()((set) => ({
  char_uuid: '',
  title: '',
  name: '',
  description: '',
  persona: '',
  model_instructions: '',
  scenario: '',
  gender: '',
  first_message: [],
  message_example: '',
  creator: '',
  creator_uuid: '',
  tags: [],
  profile_image: '',
  is_public: false,
  is_nsfw: false,
  is_owner: false,
  is_details_private: false,
  is_banned: false,
  lorebook: '',
  attribute: '',
  profileImageFile: null,
  max_alternatives: 5,
  tokenTotal: 0,
  permanentTokens: 0,
  temporaryTokens: 0,
  error: null,
  gallery: [],
  creatorNotes: '',

  setCharacter: (data) =>
    set((state) => {
      const nextState =
        typeof data === 'function' ? data(state) : { ...state, ...data };

      if (nextState.first_message) {
        if (typeof nextState.first_message === 'string') {
          nextState.first_message = [nextState.first_message];
        } else if (!Array.isArray(nextState.first_message)) {
          nextState.first_message = [];
        }
      } else {
        nextState.first_message = [];
      }

      if (Array.isArray(nextState.message_example)) {
        nextState.message_example = nextState.message_example.join(' ');
      }

      if (!Array.isArray(nextState.gallery)) {
        nextState.gallery = [];
      }

      return nextState;
    }),

  setGender: (gender) => set({ gender }),
  setTokenTotal: (tokenTotal) => set({ tokenTotal }),
  setPermanentTokens: (tokens) => set({ permanentTokens: tokens }),
  setTemporaryTokens: (tokens) => set({ temporaryTokens: tokens }),
  setError: (error) => set({ error }),
  setFirstMessageAlternatives: (alternatives) =>
    set({ first_message: alternatives }),
  setMaxAlternatives: (max) => set({ max_alternatives: max }),
  loadDraft: (draft) =>
    set(() => {
      let fm: string[] = [];
      if (draft.first_message) {
        fm =
          typeof draft.first_message === 'string'
            ? [draft.first_message]
            : draft.first_message;
      }
      let me = draft.message_example;
      if (Array.isArray(me)) me = me.join(' ');

      const gallery = Array.isArray(draft.gallery) ? draft.gallery : [];

      return { ...draft, first_message: fm, message_example: me, gallery };
    }),
  addGalleryImages: (urls: string[]) =>
    set((state) => ({ gallery: [...state.gallery, ...urls] })),
  setCreatorNotes: (notes: string) => set({ creatorNotes: notes }),
}));
